import { useEffect, useRef, useState } from 'react';

import { Flex } from '@revenuemarket/cds';
import { Colors, typographyStyle } from '@revenuemarket/design';
import { ResponseStatus } from '@revenuemarket/http';
import { Toast } from '@revenuemarket/ui';
import { Divider, InputRef, Typography } from 'antd';

import { GroupTreeItem } from '~apis/custom-label';
import { flattenTree } from '~components/ui/HorizontalTreeTable/Table';
import { TreeData, TreeNodeId } from '~components/ui/HorizontalTreeTable/utils';
import {
  useMoveGroupParentCategory,
  useReorderCustomLabels,
  useToggleCustomLabel,
} from '~hooks/queries/custom-labels/query';
import { useDemoAwareCompanyId } from '~hooks/useDemoAwareCompanyId';
import { MIXPANEL_EVENT } from '~libs/mixpanel/event';
import { mixpanel } from '~pages/_app';

import GroupLabelDescriptionTextArea from './GroupLabelDescriptionTextArea';
import GroupNameInput from './GroupNameInput';
import GroupTreeSelect from './GroupTreeSelect';
import InfoItem from './InfoItem';
import ActiveToggleSection from './common/component/ActiveToggleSection';
import ReorderSection from './common/component/OrderChangeSection';
import useGroupLabelTreeStore from './store/groupLabelTreeStore';

const GroupLabelInfo: React.FC<
  {
    depth: number;
    namePath: string;
    activeKey: string;
    activeChildren: TreeData<GroupTreeItem>[];
    parentKey: TreeNodeId | null;
  } & GroupTreeItem
> = ({ depth, activeChildren, activeKey, parentKey, ...props }) => {
  const { companyId } = useDemoAwareCompanyId();
  const inputRef = useRef<InputRef>(null);
  const [orderPosition, setOrderPosition] = useState<string>('');
  const [isActive, setIsActive] = useState(props.isActive);
  const { groupLabelTree, setGroupLabelTree } = useGroupLabelTreeStore();
  const flattenedTree = flattenTree(groupLabelTree).filter((item) => !!item.value?.name);

  const { mutateAsync: toggleGroupActive } = useToggleCustomLabel();
  const { mutateAsync: moveParentCategory, isLoading: isMoveParentLoading } =
    useMoveGroupParentCategory();
  const { mutateAsync: reorder, isLoading } = useReorderCustomLabels();

  const parentCategory = flattenedTree.find((item) => item.key === parentKey);

  const allRootLength = groupLabelTree.length - 1;
  const rootIndex = groupLabelTree.findIndex((item) => item.key === activeKey) + 1;
  const id = Number(activeKey);
  const groupIds =
    depth === 1
      ? groupLabelTree.filter((item) => item.status !== 'new').map((item) => item.key)
      : parentCategory?.children
          .filter((item) => item.status !== 'new')
          .map((item) => item.value.id) ?? [];

  const childrenTarget = parentCategory?.children.filter((item) => item.status !== 'new') || [];
  const childrenIndex = childrenTarget.findIndex((item) => item.key.toString() === activeKey) ?? 0;

  const orderUpDisable = depth === 1 ? rootIndex === 1 : childrenIndex === 0;
  const orderDownDisable =
    depth === 1 ? allRootLength === rootIndex : childrenIndex === (childrenTarget.length ?? 0) - 1;

  const parentTarget = flattenedTree.find((item) => item.key === parentKey);
  const childrenTree = flattenTree(activeChildren ?? []).filter((item) => item.children.length > 0);
  const filteredChildrenTree = [parentTarget, ...childrenTree].map((item) => item?.key);
  const wrapperRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    setIsActive(props.isActive);
  }, [props.description, props.isActive]);

  const onMoveParentCategory = async (newId: number) => {
    const response = await moveParentCategory({
      companyId,
      id,
      newParentKey: newId,
    });
    if (response.Status === ResponseStatus.SUCCESS) {
      mixpanel.track(MIXPANEL_EVENT.GROUP.CHANGE_PARENT_GROUP);
      mixpanel.track(MIXPANEL_EVENT.ENGAGEMENT.DEFAULT, {
        type: 'CHANGE_PARENT_GROUP',
      });
      Toast.success('상위 항목이 변경되었습니다.');
    } else {
      Toast.error('상위 항목 변경에 실패했습니다.');
    }
  };

  const onReorder = async (position: number) => {
    // groupIds에서 기존 index를 찾아서 position으로 변경
    function moveTargetToIndex(arr: number[], newIndex: number) {
      const index = arr.indexOf(props.id); // target의 현재 인덱스 찾기
      if (index === -1 || newIndex < 0 || newIndex >= arr.length) {
        return arr; // target이 없거나 newIndex가 범위를 벗어나면 원본 배열 반환
      }

      // target 값을 제거하고 새로운 위치에 삽입
      arr.splice(index, 1); // 기존 위치에서 제거
      arr.splice(newIndex, 0, props.id); // 새로운 위치에 삽입

      return arr;
    }

    // 예제 사용

    const result = moveTargetToIndex(
      groupIds.map((v) => Number(v)),
      position,
    );

    const response = await reorder({
      companyId,
      request: {
        ids: result.map((id) => Number(id)),
        parentId: parentCategory?.key ? Number(parentCategory?.key) : null,
      },
    });

    if (response.Status === ResponseStatus.SUCCESS) {
      Toast.success('항목 순서가 변경되었습니다.');
    } else {
      Toast.error('항목 순서 변경에 실패했습니다.');
    }
  };

  switch (depth) {
    default:
      return (
        <Flex gap={12} direction="column">
          <InfoItem
            title="항목 이름"
            titleStyle={{
              display: 'inline-block',
              paddingTop: 6,
            }}
            description={
              <GroupNameInput
                inputRef={inputRef}
                id={id}
                parentId={
                  (parentKey as string)?.split('_')[1]
                    ? Number((parentKey as string)?.split('_')[1])
                    : null
                }
                name={props.name}
                description={props.description ?? undefined}
              />
            }
          />
          <InfoItem
            title="항목 설명 (선택)"
            titleStyle={{
              display: 'inline-block',
              paddingTop: 6,
            }}
            description={
              <GroupLabelDescriptionTextArea
                id={id}
                parentId={
                  (parentKey as string)?.split('_')[1]
                    ? Number((parentKey as string)?.split('_')[1])
                    : null
                }
                name={props.name}
                description={props.description ?? undefined}
              />
            }
          />
          <InfoItem
            title="상위 계정"
            description={
              <Flex gap={8} align="center">
                <Typography.Text
                  style={{
                    ...typographyStyle['14_400'],
                    color: Colors.gray900,
                    width: 'max-content',
                  }}
                >
                  {parentCategory?.value?.name}
                </Typography.Text>
                <GroupTreeSelect
                  treeRef={wrapperRef}
                  disableActionOnInactiveItem={false}
                  treeList={flattenedTree.map((item) => {
                    return {
                      ...item,
                      isDisabled:
                        item.key === activeKey ||
                        (filteredChildrenTree.includes(item.key) && depth - 1 !== item.depth),
                    };
                  })}
                  defaultActiveKey={parentCategory?.key ? [parentCategory?.key] : []}
                  onApply={(item) => {
                    if (!item?.value) return;
                    onMoveParentCategory(item?.value?.id);
                  }}
                  applyLoading={isMoveParentLoading}
                />
              </Flex>
            }
          />
          <ActiveToggleSection
            isActive={isActive}
            onToggle={async (checked) => {
              const response = await toggleGroupActive({
                companyId,
                id,
                active: checked,
              });
              if (response.Status === ResponseStatus.SUCCESS) {
                Toast.success('항목 활성화 여부가 변경되었습니다.');
                setIsActive(checked);
              } else {
                Toast.error('항목 활성화 여부 변경에 실패했습니다.');
              }
            }}
          />
          <ReorderSection
            orderDownDisable={orderDownDisable}
            orderUpDisable={orderUpDisable}
            orderPosition={orderPosition}
            isLoading={isLoading}
            onArrowUpClick={() => {
              const position = groupIds.findIndex(
                (groupId) => groupId.toString() === id.toString(),
              );

              setOrderPosition('up');
              onReorder(position - 1);
            }}
            onArrowDownClick={() => {
              const position = groupIds.findIndex(
                (groupId) => groupId.toString() === id.toString(),
              );

              setOrderPosition('down');
              onReorder(position + 1);
            }}
          />
          <Divider />
        </Flex>
      );
  }
};

export default GroupLabelInfo;
